import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { CheckCircle, XCircle, ClipboardText, MagnifyingGlass, ArrowSquareOut, Trash, PaperPlaneTilt, DownloadSimple, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES_PREVIEW = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const formatAnno = (y) => y ? `${y}/${String(y + 1).slice(2)}` : '-';

export function LessonConfirmationsSection({ anno }) {
  const [confirmations, setConfirmations] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sendingId] = useState(null);

  // Bulk send modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ done: 0, total: 0 });

  // Single-student preview modal state
  const [previewStudent, setPreviewStudent] = useState(null);
  const [previewTemplates, setPreviewTemplates] = useState([]);
  const [previewTemplateId, setPreviewTemplateId] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSending, setPreviewSending] = useState(false);
  const [listini, setListini] = useState([]);
  const [previewListinoId, setPreviewListinoId] = useState('');

  // Modale Tariffe (assegnazione listino per ogni lezione dell'alunno)
  const [tariffeStudent, setTariffeStudent] = useState(null);
  const [tariffeLessons, setTariffeLessons] = useState([]);
  const [tariffeLoading, setTariffeLoading] = useState(false);
  const [tariffeAllListini, setTariffeAllListini] = useState([]);
  const [studentsListinoMap, setStudentsListinoMap] = useState({}); // studentId -> bool (has tutte assegnate)

  const openTariffe = async (student) => {
    setTariffeStudent(student);
    setTariffeLoading(true);
    try {
      const targetYear = anno ? anno + 1 : undefined;
      const [lRes, lstRes] = await Promise.all([
        api.getStudentLessonsWithListino(student.id, anno),
        api.getListini(targetYear),
      ]);
      setTariffeLessons(lRes.data || []);
      let lis = lstRes.data || [];
      // Fallback: se non ci sono listini per l'anno target, mostra tutti
      if (lis.length === 0) {
        try { const all = await api.getListini(); lis = all.data || []; } catch { /* ignore */ }
      }
      setTariffeAllListini(lis);
    } catch { toast.error('Errore caricamento lezioni'); }
    finally { setTariffeLoading(false); }
  };

  const updateTariffaLesson = async (lessonId, listinoId) => {
    try {
      await api.setLessonListino(lessonId, listinoId);
      const updatedLessons = tariffeLessons.map(l => l.id === lessonId ? { ...l, listino_id: listinoId, listino: tariffeAllListini.find(x => x.id === listinoId) } : l);
      setTariffeLessons(updatedLessons);
      // Aggiorna mappa studenti IMMEDIATAMENTE in locale (no fetch)
      if (tariffeStudent) {
        const allSet = updatedLessons.length > 0 && updatedLessons.every(l => l.listino_id);
        setStudentsListinoMap(prev => ({ ...prev, [tariffeStudent.id]: allSet }));
      }
    } catch { toast.error('Errore aggiornamento listino'); }
  };

  const loadStudentsListinoStatus = async () => {
    // Per ogni alunno con email controllo:
    //  - se la conferma ha un listino_id (dato legacy via vecchio popup) => OK
    //  - oppure se TUTTE le lezioni hanno listino_id (dato nuovo per-lezione) => OK
    const map = {};
    const confByStudent = {};
    confirmations.forEach(c => { if (c.student_id) confByStudent[c.student_id] = c; });
    for (const s of allStudents.filter(s => (s.emails || []).some(em => em.address))) {
      const conf = confByStudent[s.id];
      if (conf && conf.listino_id) {
        map[s.id] = true;
        continue;
      }
      try {
        const r = await api.getStudentLessonsWithListino(s.id, anno);
        const lessons = r.data || [];
        map[s.id] = lessons.length > 0 && lessons.every(l => l.listino_id);
      } catch { map[s.id] = false; }
    }
    setStudentsListinoMap(map);
  };

  useEffect(() => {
    if (allStudents.length) loadStudentsListinoStatus();
    // eslint-disable-next-line
  }, [allStudents, confirmations]);

  // Carica template Conferma Iscrizione (seed solo se non esiste)
  const ensureTemplate = async () => {
    const tplRes = await api.getEmailTemplates();
    let tpls = tplRes.data || [];
    let tpl = tpls.find(t => t.name === 'Conferma Iscrizione - Istruzioni per il pagamento');
    if (!tpl) {
      await api.seedConfermaTemplate();
      const tplRes2 = await api.getEmailTemplates();
      tpls = tplRes2.data || [];
      tpl = tpls.find(t => t.name === 'Conferma Iscrizione - Istruzioni per il pagamento');
    }
    return tpl;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = anno ? { source_year: anno } : {};
      const [confRes, stRes] = await Promise.all([
        api.getLessonConfirmations(params),
        api.getAdminAllStudents({ anno }),
      ]);
      setConfirmations(confRes.data || []);
      // Includo tutti gli alunni dell'anno (filtraggio email avviene a livello UI quando serve)
      setAllStudents(stRes.data || []);
    } catch { toast.error('Errore caricamento dati'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [anno]); // eslint-disable-line react-hooks/exhaustive-deps

  const STATUS_FLAGS_ORDER = ['i', 'r1', 'r2', 'r3'];
  const STATUS_FLAG_LABELS = { i: 'I', r1: 'R1', r2: 'R2', r3: 'R3' };
  const DAY_NAMES_SHORT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  const toggleLessonFlag = async (studentId, lessonId, flag, currentActive) => {
    const newActive = !currentActive;
    // Ottimistico
    setAllStudents(prev => prev.map(s => {
      if (s.id !== studentId) return s;
      return {
        ...s,
        lessons: (s.lessons || []).map(l => {
          if (l.id !== lessonId) return l;
          const set = new Set(l.status_flags || []);
          if (newActive) set.add(flag); else set.delete(flag);
          const ordered = STATUS_FLAGS_ORDER.filter(f => set.has(f));
          return { ...l, status_flags: ordered };
        }),
      };
    }));
    try {
      await api.setLessonStatusFlag(lessonId, flag, newActive);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento flag');
      loadAll();
    }
  };

  // Merge: per ogni alunno, trova la sua conferma (può essere undefined)
  const rows = allStudents.map(s => {
    const conf = confirmations.find(c => c.student_id === s.id || (c.student_name || '').trim().toUpperCase() === (s.name || '').trim().toUpperCase());
    return { student: s, conf };
  }).sort((a, b) => (a.student.name || '').localeCompare(b.student.name || ''));

  const openPreviewForStudent = async (student) => {
    setPreviewStudent(student);
    setPreviewSubject('');
    setPreviewBody('');
    setPreviewLoading(true);
    const recipients = (student.emails || []).filter(em => em.address).map(em => em.address);
    setPreviewRecipients(recipients);
    try {
      // Carica i template aggiornati dal DB (NON chiamiamo più seed qui per non
      // rischiare di sovrascrivere le personalizzazioni dell'utente)
      const [tplRes, listiniRes] = await Promise.all([
        api.getEmailTemplates(),
        api.getListini(anno + 1).catch(() => ({ data: [] })),
      ]);
      let templates = tplRes.data || [];
      // Se non esiste ancora il template "Conferma Iscrizione - Istruzioni per il pagamento", lo creo (seed crea solo se manca)
      if (!templates.find(t => t.name === 'Conferma Iscrizione - Istruzioni per il pagamento')) {
        try {
          await api.seedConfermaTemplate();
          const tplRes2 = await api.getEmailTemplates();
          templates = tplRes2.data || [];
        } catch { /* ignore */ }
      }
      setPreviewTemplates(templates);
      // Cerca listini per anno target prima, poi fallback anno corrente
      let lis = listiniRes.data || [];
      if (lis.length === 0) {
        try { const fb = await api.getListini(anno); lis = fb.data || []; } catch { /* ignore */ }
      }
      setListini(lis);
      // Se la conferma esistente ha già un listino, usalo come default
      const existingConf = confirmations.find(c => c.student_id === student.id);
      const preselectedListinoId = existingConf?.listino_id && lis.find(l => l.id === existingConf.listino_id)?.id;
      const defaultListinoId = preselectedListinoId || lis[0]?.id || '';
      setPreviewListinoId(defaultListinoId);
      // Default: Conferma Iscrizione - Istruzioni per il pagamento
      const conf = templates.find(t => t.name === 'Conferma Iscrizione - Istruzioni per il pagamento') || templates[0];
      if (conf) {
        setPreviewTemplateId(conf.id);
        await loadPreviewForTemplate(conf.id, student.id, defaultListinoId);
      }
    } catch { toast.error('Errore caricamento template'); }
    finally { setPreviewLoading(false); }
  };

  const loadPreviewForTemplate = async (templateId, studentId, listinoId) => {
    setPreviewLoading(true);
    try {
      const res = await api.previewEmail({ student_id: studentId, template_id: templateId, listino_id: listinoId });
      setPreviewSubject(res.data.subject || '');
      setPreviewBody(res.data.body || '');
    } catch { toast.error('Errore anteprima'); }
    finally { setPreviewLoading(false); }
  };

  const handleTemplateChange = (templateId) => {
    setPreviewTemplateId(templateId);
    if (previewStudent) loadPreviewForTemplate(templateId, previewStudent.id, previewListinoId);
  };

  const handleListinoChange = (listinoId) => {
    setPreviewListinoId(listinoId);
    if (previewStudent && previewTemplateId) loadPreviewForTemplate(previewTemplateId, previewStudent.id, listinoId);
  };

  const sendFromPreview = async () => {
    if (!previewStudent || !previewTemplateId) return;
    if (!previewRecipients.length) { toast.error('Nessun destinatario'); return; }
    setPreviewSending(true);
    try {
      await api.sendEmail({
        student_id: previewStudent.id,
        template_id: previewTemplateId,
        recipients: previewRecipients,
        subject: previewSubject,
        body: previewBody,
        listino_id: previewListinoId || null,
      });
      toast.success(`Mail inviata a ${previewStudent.name.toUpperCase()}`);
      setPreviewStudent(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio');
    } finally { setPreviewSending(false); }
  };

  const openSendModal = () => {
    setShowSendModal(true);
    setSelectedStudents([]);
  };

  const toggleStudent = (id) => {
    setSelectedStudents(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const visible = allStudents
      .filter(s => (s.emails || []).some(em => em.address))
      .filter(s => !studentSearch || s.name.toLowerCase().includes(studentSearch.toLowerCase()))
      .map(s => s.id);
    const allSel = visible.every(id => selectedStudents.includes(id));
    setSelectedStudents(allSel ? selectedStudents.filter(id => !visible.includes(id)) : Array.from(new Set([...selectedStudents, ...visible])));
  };

  const handleSendConferme = async () => {
    if (!selectedStudents.length) { toast.error('Seleziona almeno un alunno'); return; }
    setSending(true);
    setSendProgress({ done: 0, total: selectedStudents.length });
    try {
      const tpl = await ensureTemplate();
      if (!tpl) { toast.error('Template non disponibile'); setSending(false); return; }
      let success = 0; let errors = 0;
      for (const sid of selectedStudents) {
        const s = allStudents.find(x => x.id === sid);
        if (!s) continue;
        const recipients = (s.emails || []).filter(em => em.address).map(em => em.address);
        if (!recipients.length) { errors++; continue; }
        try {
          await api.sendEmail({ student_id: sid, template_id: tpl.id, recipients });
          success++;
        } catch { errors++; }
        setSendProgress(p => ({ ...p, done: p.done + 1 }));
      }
      toast.success(`Inviate ${success} conferme${errors ? ` (${errors} errori)` : ''}`);
      setShowSendModal(false);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio massivo');
    } finally { setSending(false); }
  };

  const openLink = (token) => {
    const url = `${window.location.origin}/conferma/${token}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Eliminare la conferma di "${it.student_name.toUpperCase()}"?\nL'alunno non potrà più aprire il link.`)) return;
    try {
      await api.deleteLessonConfirmation(it.id);
      setConfirmations(prev => prev.filter(i => i.id !== it.id));
      toast.success('Conferma eliminata');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore eliminazione'); }
  };

  const handleDeleteAllFiltered = async () => {
    const confs = filteredRows.map(r => r.conf).filter(Boolean);
    if (!confs.length) { toast.info('Nessuna conferma da cancellare'); return; }
    if (!window.confirm(`Eliminare ${confs.length} conferme filtrate?\nQuesta operazione non è reversibile.`)) return;
    try {
      const ids = confs.map(c => c.id);
      await Promise.all(ids.map(id => api.deleteLessonConfirmation(id)));
      setConfirmations(prev => prev.filter(i => !ids.includes(i.id)));
      toast.success(`${ids.length} conferme eliminate`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore eliminazione massiva'); }
  };

  const handleResetAll = async () => {
    if (!confirmations.length) { toast.info('Nessuna conferma da cancellare'); return; }
    if (!window.confirm(`Eliminare TUTTE le ${confirmations.length} conferme iscrizione?\nQuesta operazione non è reversibile.\nDopo, potrai inviare nuove mail e gli alunni ricominceranno da zero.`)) return;
    try {
      await Promise.all(confirmations.map(i => api.deleteLessonConfirmation(i.id)));
      setConfirmations([]);
      toast.success('Tutte le conferme sono state eliminate');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore reset'); }
  };

  const handlePaymentStatusChange = async (confId, newStatus) => {
    try {
      await api.updateConfirmationPaymentStatus(confId, newStatus);
      setConfirmations(prev => prev.map(c => c.id === confId ? { ...c, payment_status: newStatus } : c));
      toast.success('Stato pagamento aggiornato');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento stato'); }
  };

  const downloadBonifico = async (conf) => {
    try {
      const res = await api.downloadBonifico(conf.id);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contabile_${conf.student_name.replace(/\s+/g, '_')}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore download contabile'); }
  };

  // Ritorna label testuale dello stato per la stampa/export
  const statusLabel = (conf) => {
    if (!conf) return 'Non inviata';
    if (conf.status === 'pending') return 'Inviata';
    if (conf.status === 'responded') {
      const lc = conf.lesson_choices || [];
      const anyYes = lc.some(c => (c.choice || (c.confirmed === true ? 'yes' : '')) === 'yes') || conf.confirmed === true;
      if (!anyYes) {
        if (lc.length > 0 && lc.some(c => (c.choice || '') === 'wait')) return 'Attende 2° slot';
        return 'Non confermata';
      }
      const ps = conf.payment_status || 'ricevuta';
      return {
        ricevuta: 'Ricevuta',
        inviato_dettagli: 'Inviato dettagli pagamento',
        ricevuto_bonifico: 'Ricevuto bonifico',
        verificata_pagata: 'Verificato pagato',
      }[ps] || 'Ricevuta';
    }
    return '-';
  };

  // Label leggibile del filtro stato attivo (per header di stampa/export)
  const statusFilterLabel = () => ({
    all: 'Tutti',
    'not-sent': 'Non inviato',
    ricevuta: 'Ricevuta',
    inviato_dettagli: 'Inviato dettagli pagamento',
    ricevuto_bonifico: 'Ricevuto bonifico',
    verificata_pagata: 'Verificato pagato',
  }[statusFilter] || 'Tutti');

  // Costruisce le righe da stampare/esportare a partire dalle righe attualmente visualizzate (filteredRows)
  const buildExportRows = () => filteredRows.map(({ student, conf }) => ({
    alunno: student.name,
    insegnante: (conf?.teacher_names || []).join(', ') || '-',
    email: (student.emails || []).map(em => em.address).filter(Boolean).join(', ') || '-',
    listino: (conf?.listini_codes && conf.listini_codes.length) ? conf.listini_codes.join(', ') : '-',
    stato: statusLabel(conf),
    risposta: conf?.responded_at ? new Date(conf.responded_at).toLocaleDateString('it-IT') : '-',
  }));

  const handlePrintFiltered = () => {
    const rowsToPrint = buildExportRows();
    if (!rowsToPrint.length) { toast.error('Nessuna riga da stampare'); return; }
    const today = new Date().toLocaleDateString('it-IT');
    const filtriApplicati = [];
    if (statusFilter !== 'all') filtriApplicati.push(`Stato: ${statusFilterLabel()}`);
    if (search) filtriApplicati.push(`Ricerca: "${search}"`);
    const filtriHtml = filtriApplicati.length ? `<div style="font-size:10px;color:#666;margin-top:2px">Filtri: ${filtriApplicati.join(' \u2022 ')}</div>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Conferme Iscrizioni ${anno}/${(anno+1).toString().slice(-2)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 24px; color: #1a1a1a; }
  header { display:flex; justify-content: space-between; align-items:flex-end; border-bottom: 2px solid #2D2A4A; padding-bottom: 8px; margin-bottom: 16px; }
  h1 { font-size: 20px; margin: 0; color: #2D2A4A; letter-spacing: 0.5px; }
  .brand { font-size: 11px; color: #666; margin-top: 4px; font-weight: 600; letter-spacing: 0.3px; }
  .meta { font-size: 11px; color: #666; text-align: right; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { background: #F4F4F5; border-bottom: 1px solid #ccc; text-align: left; padding: 8px; text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; color: #52525B; }
  td { border-bottom: 1px solid #E4E4E7; padding: 8px; vertical-align: middle; }
  td.alunno { font-weight: 600; text-transform: uppercase; }
  td.stato { font-weight: 600; }
  .stato-non-inviata { color: #2563eb; }
  .stato-inviata { color: #b45309; }
  .stato-ricevuta { color: #4338ca; }
  .stato-inviato-dettagli-pagamento { color: #7e22ce; }
  .stato-ricevuto-bonifico { color: #c2410c; }
  .stato-verificato-pagato { color: #16a34a; }
  .stato-non-confermata { color: #b91c1c; }
  .stato-attende-2-slot { color: #b45309; }
  footer { margin-top: 24px; font-size: 10px; color: #999; text-align: center; }
  @page { size: A4 landscape; margin: 12mm; }
  @media print { body { margin: 0; } }
</style></head><body>
<header>
  <div>
    <h1>Conferme Iscrizioni ${anno}/${(anno+1).toString().slice(-2)}</h1>
    <div class="brand">Fiori Musicali</div>
    ${filtriHtml}
  </div>
  <div class="meta">Stampato il ${today}<br/>Totale: ${rowsToPrint.length} alunni</div>
</header>
<table>
  <thead><tr><th style="width:32px">#</th><th>Alunno</th><th>Insegnante</th><th>Email</th><th>Listino</th><th>Stato</th><th>Risposta</th></tr></thead>
  <tbody>
    ${rowsToPrint.map((r, i) => {
      const statoClass = 'stato-' + r.stato.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return `<tr><td>${i+1}</td><td class="alunno">${r.alunno}</td><td>${r.insegnante}</td><td style="font-size:10px;color:#52525B">${r.email}</td><td>${r.listino}</td><td class="stato ${statoClass}">${r.stato}</td><td>${r.risposta}</td></tr>`;
    }).join('')}
  </tbody>
</table>
<footer>Fiori Musicali &mdash; Conferme Iscrizioni &mdash; Pagina stampata il ${today}</footer>
<script>window.onload = function() { setTimeout(function(){ window.print(); }, 200); }</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup bloccato dal browser'); return; }
    w.document.write(html);
    w.document.close();
  };

  const handleExportCSV = () => {
    const rowsToExport = buildExportRows();
    if (!rowsToExport.length) { toast.error('Nessuna riga da esportare'); return; }
    const escape = (v) => {
      const s = String(v ?? '');
      if (/[";\n,]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const headers = ['#', 'Alunno', 'Insegnante', 'Email', 'Listino', 'Stato', 'Risposta'];
    const lines = [headers.join(';')];
    rowsToExport.forEach((r, i) => {
      lines.push([i + 1, r.alunno, r.insegnante, r.email, r.listino, r.stato, r.risposta].map(escape).join(';'));
    });
    // BOM per compatibilità Excel con caratteri accentati
    const csv = '\uFEFF' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conferme_iscrizioni_${anno}_${(anno + 1).toString().slice(-2)}_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Esportate ${rowsToExport.length} righe`);
  };

  // Helper: classifica una conferma in base alle scelte degli alunni
  const hasChoice = (conf, val) => {
    const lc = conf?.lesson_choices || [];
    return lc.some(c => (c.choice || (c.confirmed === true ? 'yes' : c.confirmed === false ? 'no' : '')) === val);
  };
  const allChoice = (conf, val) => {
    const lc = conf?.lesson_choices || [];
    return lc.length > 0 && lc.every(c => (c.choice || (c.confirmed === true ? 'yes' : c.confirmed === false ? 'no' : '')) === val);
  };

  // Helper: ritorna lo stato effettivo (per filtri/contatori) di una conferma
  // Stati possibili: 'not-sent' | 'ricevuta' | 'inviato_dettagli' | 'ricevuto_bonifico' | 'verificata_pagata' | 'other'
  // 'other' = pending o responded ma non confermato (es. attende 2° slot, non confermata): non rientra nei 5 contatori
  const computeEffectiveStatus = (conf) => {
    if (!conf) return 'not-sent';
    // Se l'admin ha impostato manualmente un payment_status, quello vince (override)
    if (conf.payment_status) return conf.payment_status;
    if (conf.status !== 'responded') return 'other';
    const anyYes = (conf.lesson_choices || []).some(c => (c.choice || (c.confirmed === true ? 'yes' : '')) === 'yes') || conf.confirmed === true;
    if (!anyYes) return 'other';
    return 'ricevuta';
  };

  const stats = {
    notSent: rows.filter(r => !r.conf).length,
    ricevuta: confirmations.filter(c => computeEffectiveStatus(c) === 'ricevuta').length,
    inviatoDettagli: confirmations.filter(c => c.payment_status === 'inviato_dettagli').length,
    ricevutoBonifico: confirmations.filter(c => c.payment_status === 'ricevuto_bonifico').length,
    verificataPagata: confirmations.filter(c => c.payment_status === 'verificata_pagata').length,
  };

  // Filtri sui rows
  const filteredRows = rows.filter(r => {
    if (statusFilter !== 'all') {
      const eff = computeEffectiveStatus(r.conf);
      if (eff !== statusFilter) return false;
    }
    if (search && !(r.student.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Conferme Iscrizioni {anno ? `${formatAnno(anno)} \u00BB ${formatAnno(anno + 1)}` : ''}</h1>
          <p className="text-[#52525B] text-sm">Risposte degli alunni alla richiesta di rinnovo iscrizione</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openSendModal} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="open-send-confirmations-btn">
            <PaperPlaneTilt size={16} className="mr-1" />Invia Conferma Iscrizione
          </Button>
          {confirmations.length > 0 && (
            <Button variant="outline" onClick={handleResetAll} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="reset-all-confirmations-btn">
              <Trash size={16} className="mr-1" />Reset tutte ({confirmations.length})
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <button onClick={() => setStatusFilter('not-sent')} className={`bg-white border rounded-sm p-3 text-left transition-all ${statusFilter === 'not-sent' ? 'border-blue-500 ring-1 ring-blue-300' : 'border-[#E4E4E7]'}`} data-testid="filter-not-sent">
          <div className="text-[10px] font-semibold uppercase text-blue-700 tracking-wider">Non inviato</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{stats.notSent}</div>
        </button>
        <button onClick={() => setStatusFilter('ricevuta')} className={`bg-white border rounded-sm p-3 text-left transition-all ${statusFilter === 'ricevuta' ? 'border-indigo-500 ring-1 ring-indigo-300' : 'border-[#E4E4E7]'}`} data-testid="filter-ricevuta">
          <div className="text-[10px] font-semibold uppercase text-indigo-700 tracking-wider">Ricevuta</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{stats.ricevuta}</div>
        </button>
        <button onClick={() => setStatusFilter('inviato_dettagli')} className={`bg-white border rounded-sm p-3 text-left transition-all ${statusFilter === 'inviato_dettagli' ? 'border-purple-500 ring-1 ring-purple-300' : 'border-[#E4E4E7]'}`} data-testid="filter-inviato-dettagli">
          <div className="text-[10px] font-semibold uppercase text-purple-700 tracking-wider">Inviato dettagli pagamento</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{stats.inviatoDettagli}</div>
        </button>
        <button onClick={() => setStatusFilter('ricevuto_bonifico')} className={`bg-white border rounded-sm p-3 text-left transition-all ${statusFilter === 'ricevuto_bonifico' ? 'border-orange-500 ring-1 ring-orange-300' : 'border-[#E4E4E7]'}`} data-testid="filter-ricevuto-bonifico">
          <div className="text-[10px] font-semibold uppercase text-orange-700 tracking-wider">Ricevuto bonifico</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{stats.ricevutoBonifico}</div>
        </button>
        <button onClick={() => setStatusFilter('verificata_pagata')} className={`bg-white border rounded-sm p-3 text-left transition-all ${statusFilter === 'verificata_pagata' ? 'border-green-500 ring-1 ring-green-300' : 'border-[#E4E4E7]'}`} data-testid="filter-verificata-pagata">
          <div className="text-[10px] font-semibold uppercase text-green-700 tracking-wider">Verificato pagato</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{stats.verificataPagata}</div>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative w-full sm:w-[260px]">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <Input type="text" placeholder="Cerca alunno..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full rounded-sm h-9 text-sm pl-8 uppercase" data-testid="confirmations-search" />
        </div>
        {statusFilter !== 'all' && <Button variant="outline" size="sm" onClick={() => setStatusFilter('all')} className="rounded-sm h-9 text-sm">Mostra Tutti</Button>}
        {filteredRows.length > 0 && (
          <Button variant="outline" size="sm" onClick={handlePrintFiltered} className="rounded-sm h-9 text-sm text-[#2D2A4A] border-[#2D2A4A] hover:bg-indigo-50" data-testid="print-filtered-btn" title="Stampa/PDF dell'elenco attualmente visualizzato">
            <Printer size={14} className="mr-1" />Stampa elenco ({filteredRows.length})
          </Button>
        )}
        {filteredRows.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="rounded-sm h-9 text-sm text-[#2D2A4A] border-[#2D2A4A] hover:bg-indigo-50" data-testid="export-csv-filtered-btn" title="Esporta CSV dell'elenco attualmente visualizzato (apribile con Excel)">
            <DownloadSimple size={14} className="mr-1" />Esporta CSV
          </Button>
        )}
        {selectedStudents.length > 0 && (
          <select
            onChange={async (e) => {
              const newStatus = e.target.value;
              if (!newStatus) return;
              e.target.value = '';
              const confByStudent = {};
              confirmations.forEach(c => { if (c.student_id) confByStudent[c.student_id] = c; });
              const targets = selectedStudents.map(sid => confByStudent[sid]).filter(Boolean);
              if (!targets.length) { toast.error('Nessun alunno selezionato ha una conferma da aggiornare'); return; }
              const label = { ricevuta: 'Ricevuta', inviato_dettagli: 'Inviato dettagli pagamento', ricevuto_bonifico: 'Ricevuto bonifico', verificata_pagata: 'Verificato pagato' }[newStatus] || newStatus;
              if (!window.confirm(`Imposta lo stato "${label}" per ${targets.length} alunno/i selezionato/i?`)) return;
              try {
                await Promise.all(targets.map(c => api.updateConfirmationPaymentStatus(c.id, newStatus)));
                setConfirmations(prev => prev.map(c => targets.find(t => t.id === c.id) ? { ...c, payment_status: newStatus } : c));
                toast.success(`Stato aggiornato per ${targets.length} alunno/i`);
              } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento massivo'); }
            }}
            defaultValue=""
            className="rounded-sm h-9 text-sm px-3 border border-[#2D2A4A] text-[#2D2A4A] bg-white hover:bg-indigo-50 font-semibold cursor-pointer"
            data-testid="bulk-status-select"
          >
            <option value="" disabled>Cambia stato ({selectedStudents.length})...</option>
            <option value="ricevuta">Ricevuta</option>
            <option value="inviato_dettagli">Inviato dettagli pagamento</option>
            <option value="ricevuto_bonifico">Ricevuto bonifico</option>
            <option value="verificata_pagata">Verificato pagato</option>
          </select>
        )}
        {filteredRows.some(r => r.conf) && (
          <Button variant="outline" size="sm" onClick={handleDeleteAllFiltered} className="rounded-sm h-9 text-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-all-filtered-btn">
            <Trash size={14} className="mr-1" />Elimina filtrate ({filteredRows.filter(r => r.conf).length})
          </Button>
        )}
        <div className="ml-auto bg-white border border-[#E4E4E7] rounded-sm px-4 py-1.5 text-sm">
          <span className="text-[#52525B]">Totale:</span> <span className="font-bold font-['Work_Sans']">{filteredRows.length}</span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
      ) : filteredRows.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm">
          <ClipboardText size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">Nessun alunno corrispondente</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="confirmations-table">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="w-10 text-center px-2 py-2">
                  <Checkbox
                    checked={filteredRows.length > 0 && filteredRows.every(r => selectedStudents.includes(r.student.id))}
                    onCheckedChange={() => {
                      const visible = filteredRows.map(r => r.student.id);
                      const allSel = visible.every(id => selectedStudents.includes(id));
                      setSelectedStudents(allSel ? selectedStudents.filter(id => !visible.includes(id)) : Array.from(new Set([...selectedStudents, ...visible])));
                    }}
                    data-testid="select-all-rows"
                  />
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Alunno</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Insegnante</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Lezioni / Flag</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Email</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Listino</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Stato</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Risposta</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Link</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Tariffe</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Invia</th>
                <th className="text-center px-2 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Elimina</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(({ student, conf }) => {
                const emails = (student.emails || []).map(em => em.address).filter(Boolean);
                const isSending = sendingId === student.id;
                const lc = conf?.lesson_choices || [];
                const tot = lc.length;
                const cnf = lc.filter(c => c.confirmed === true).length;
                let btnLabel = 'Invia';
                let btnClass = 'bg-blue-600 hover:bg-blue-700 text-white';
                if (conf?.status === 'pending') { btnLabel = 'Invia'; btnClass = 'bg-amber-500 hover:bg-amber-600 text-white'; }
                if (conf?.status === 'responded') { btnLabel = 'Invia'; btnClass = 'bg-[#52525B] hover:bg-[#3F3F46] text-white'; }
                const tariffeOk = studentsListinoMap[student.id] === true;
                return (
                  <tr key={student.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                    <td className="w-10 text-center px-2 py-2">
                      <Checkbox
                        checked={selectedStudents.includes(student.id)}
                        onCheckedChange={() => toggleStudent(student.id)}
                        data-testid={`row-checkbox-${student.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 uppercase font-medium" data-testid={`row-name-${student.id}`}>{student.name}</td>
                    <td className="px-3 py-2 text-xs uppercase text-[#52525B]">{(conf?.teacher_names || []).join(', ') || '-'}</td>
                    <td className="px-2 py-1.5" data-testid={`row-lessons-${student.id}`}>
                      {student.lessons?.length ? (
                        <div className="space-y-0.5">
                          {student.lessons.map(l => {
                            const active = new Set(l.status_flags || []);
                            return (
                              <div key={l.id} className="flex items-center gap-1.5 text-[10px]">
                                <div className="text-[9px] text-[#71717A] leading-tight min-w-[70px] whitespace-nowrap">{DAY_NAMES_SHORT[l.day] || '?'} {l.start_time}-{l.end_time}</div>
                                <div className="flex gap-0.5">
                                  {STATUS_FLAGS_ORDER.map(f => {
                                    const isOn = active.has(f);
                                    return (
                                      <button
                                        key={f}
                                        type="button"
                                        onClick={() => toggleLessonFlag(student.id, l.id, f, isOn)}
                                        className={`h-5 min-w-[20px] px-1 text-[9px] font-extrabold uppercase tracking-tighter rounded border transition-colors ${isOn ? 'bg-green-500 text-white border-green-600' : 'bg-white text-[#71717A] border-[#D4D4D8] hover:bg-[#F4F4F5]'}`}
                                        title={`${STATUS_FLAG_LABELS[f]}: ${isOn ? 'attivo' : 'non attivo'}`}
                                        data-testid={`conf-flag-btn-${l.id}-${f}`}
                                      >{STATUS_FLAG_LABELS[f]}</button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <span className="text-[#D4D4D8] text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-[#52525B] truncate max-w-[280px]" title={emails.join(', ')}>{emails.join(', ')}</td>
                    <td className="px-2 py-2 text-center text-xs">
                      {(conf?.listini_codes && conf.listini_codes.length > 0)
                        ? <span className="font-semibold font-['Work_Sans']" title={conf.listini_codes.join(', ')}>{conf.listini_codes.join(', ')}</span>
                        : <span className="text-[#A1A1AA]">—</span>}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {!conf && <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">Non inviata</span>}
                      {conf?.status === 'pending' && !conf?.payment_status && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Inviata</span>}
                      {conf && (conf.status === 'responded' || conf.payment_status) && (() => {
                        const hasPayment = !!conf.payment_status;
                        if (!hasPayment && tot === 0 && conf.confirmed === false) {
                          return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded"><XCircle size={14} weight="fill" /> Non confermata</span>;
                        }
                        if (!hasPayment && tot > 0 && allChoice(conf, 'no')) {
                          return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded"><XCircle size={14} weight="fill" /> Non confermata</span>;
                        }
                        if (!hasPayment && tot > 0 && hasChoice(conf, 'wait') && !hasChoice(conf, 'yes')) {
                          return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Attende 2° slot</span>;
                        }
                        // L'alunno ha confermato almeno una lezione OPPURE l'admin ha impostato manualmente uno stato → dropdown payment_status
                        const cur = conf.payment_status || 'ricevuta';
                        const colorClass = {
                          ricevuta: 'bg-blue-50 text-blue-700 border-blue-300',
                          inviato_dettagli: 'bg-purple-50 text-purple-700 border-purple-300',
                          ricevuto_bonifico: 'bg-orange-50 text-orange-700 border-orange-300',
                          verificata_pagata: 'bg-green-50 text-green-700 border-green-300',
                        }[cur] || 'bg-gray-50 text-gray-700 border-gray-300';
                        return (
                          <div className="inline-flex items-center gap-1">
                            <select
                              value={cur}
                              onChange={(e) => handlePaymentStatusChange(conf.id, e.target.value)}
                              className={`text-xs font-semibold px-2 py-1 rounded border ${colorClass} focus:outline-none focus:ring-1 focus:ring-indigo-300`}
                              title="Cambia stato pagamento"
                              data-testid={`payment-status-${conf.id}`}
                            >
                              <option value="ricevuta">Ricevuta</option>
                              <option value="inviato_dettagli">Inviato dettagli pagamento</option>
                              <option value="ricevuto_bonifico">Ricevuto bonifico</option>
                              <option value="verificata_pagata">Verificata pagata</option>
                            </select>
                            {conf.bonifico_uploaded && (
                              <button
                                onClick={() => downloadBonifico(conf)}
                                className="p-1 rounded hover:bg-[#F4F4F5] text-[#52525B]"
                                title="Scarica contabile bonifico"
                                data-testid={`download-bonifico-${conf.id}`}
                              >
                                <DownloadSimple size={14} />
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2 text-center text-xs text-[#52525B] font-['Work_Sans']">{conf?.responded_at ? new Date(conf.responded_at).toLocaleDateString('it-IT') : '-'}</td>
                    <td className="px-2 py-2 text-center">
                      {conf
                        ? <button onClick={() => openLink(conf.token)} className="p-1.5 rounded hover:bg-[#F4F4F5] text-[#52525B]" title="Apri link conferma in nuova scheda" data-testid={`open-link-${conf.id}`}><ArrowSquareOut size={14} /></button>
                        : <span className="text-[10px] text-[#A1A1AA]">—</span>}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => openTariffe(student)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-sm transition-colors ${tariffeOk ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
                        title={tariffeOk ? 'Tariffe impostate' : 'Imposta tariffe per ogni lezione'}
                        data-testid={`tariffe-btn-${student.id}`}
                      >
                        {tariffeOk ? '✓ Tariffe' : '! Tariffe'}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => tariffeOk ? openPreviewForStudent(student) : toast.error('Imposta prima le tariffe per ogni lezione')}
                        disabled={isSending}
                        className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${btnClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                        title={tariffeOk ? 'Apri anteprima e invia mail' : 'Imposta prima le tariffe'}
                        data-testid={`send-conferma-${student.id}`}
                      >
                        {isSending ? 'Invio...' : btnLabel}
                      </button>
                      {conf?.last_email_sent_at && (
                        <div className="text-[9px] text-[#71717A] leading-tight mt-0.5" title={conf.last_email_template} data-testid={`last-sent-${student.id}`}>
                          <div>{new Date(conf.last_email_sent_at).toLocaleDateString('it-IT')} {new Date(conf.last_email_sent_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
                          <div className="truncate max-w-[140px]">{conf.last_email_template}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {conf
                        ? <button onClick={() => handleDelete(conf)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Elimina conferma" data-testid={`delete-confirmation-${conf.id}`}><Trash size={14} /></button>
                        : <span className="text-[10px] text-[#A1A1AA]">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Send Conferma Iscrizione Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans'] flex items-center gap-2"><PaperPlaneTilt size={20} /> Invia Conferma Iscrizione {anno && `(${anno}/${String(anno + 1).slice(2)} → ${anno + 1}/${String(anno + 2).slice(2)})`}</DialogTitle>
            <DialogDescription>
              Seleziona gli alunni a cui inviare la mail di conferma iscrizione per l&apos;anno successivo. Ogni alunno riceverà un link unico per confermare o rifiutare le proprie lezioni.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <Input value={studentSearch} onChange={e => setStudentSearch(e.target.value)} placeholder="Cerca alunno..." className="rounded-sm pl-8" data-testid="send-student-search" />
            </div>
            <Button type="button" variant="outline" onClick={toggleSelectAll} className="rounded-sm text-xs h-9">Seleziona/Deseleziona visibili</Button>
          </div>

          <div className="flex-1 overflow-y-auto border border-[#E4E4E7] rounded-sm">
            {loading ? (
              <p className="p-4 text-sm text-[#52525B] text-center">Caricamento alunni...</p>
            ) : allStudents.filter(s => (s.emails || []).some(em => em.address)).length === 0 ? (
              <p className="p-4 text-sm text-[#52525B] text-center">Nessun alunno con email disponibile per l&apos;anno {anno}.</p>
            ) : (
              <ul className="divide-y divide-[#E4E4E7]">
                {allStudents
                  .filter(s => (s.emails || []).some(em => em.address))
                  .filter(s => !studentSearch || s.name.toLowerCase().includes(studentSearch.toLowerCase()))
                  .map(s => {
                    const emails = (s.emails || []).filter(em => em.address);
                    return (
                      <li key={s.id} className="px-3 py-2 flex items-center gap-3 hover:bg-[#FAFAFA]">
                        <Checkbox checked={selectedStudents.includes(s.id)} onCheckedChange={() => toggleStudent(s.id)} data-testid={`select-student-${s.id}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium uppercase truncate">{s.name}</p>
                          <p className="text-xs text-[#52525B] truncate">{emails.map(em => em.address).join(', ')}</p>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>

          <DialogFooter className="pt-2">
            <div className="text-xs text-[#52525B] mr-auto">
              {sending ? `Invio ${sendProgress.done}/${sendProgress.total}...` : `${selectedStudents.length} alunno/i selezionati`}
            </div>
            <Button type="button" variant="outline" onClick={() => setShowSendModal(false)} className="rounded-sm">Annulla</Button>
            <Button type="button" onClick={handleSendConferme} disabled={sending || !selectedStudents.length} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="send-confirmations-submit-btn">
              <PaperPlaneTilt size={16} className="mr-1" />{sending ? 'Invio...' : `Invia (${selectedStudents.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-student Preview & Send Modal */}
      <Dialog open={!!previewStudent} onOpenChange={(o) => { if (!o) setPreviewStudent(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans'] flex items-center gap-2"><PaperPlaneTilt size={20} /> Anteprima Mail Conferma Iscrizione</DialogTitle>
            <DialogDescription>
              Destinatario: <strong className="uppercase">{previewStudent?.name}</strong> ({previewRecipients.join(', ') || 'nessuna email'}). Scegli il modello, controlla l&apos;anteprima, modifica oggetto/corpo se serve, poi invia.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 overflow-y-auto flex-1 pr-1">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Modello</label>
              <select value={previewTemplateId} onChange={(e) => handleTemplateChange(e.target.value)} className="w-full mt-1 rounded-sm border border-[#E4E4E7] px-3 py-2 text-sm" data-testid="preview-template-select">
                {previewTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Oggetto</label>
              <Input value={previewSubject} onChange={(e) => setPreviewSubject(e.target.value)} className="rounded-sm mt-1" disabled={previewLoading} data-testid="preview-subject-input" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Corpo</label>
              <div className="border border-[#E4E4E7] rounded-sm bg-white mt-1" data-testid="preview-body-input">
                <ReactQuill
                  value={previewBody}
                  onChange={(v) => setPreviewBody(v)}
                  modules={QUILL_MODULES_PREVIEW}
                  theme="snow"
                  readOnly={previewLoading}
                  style={{ minHeight: 280 }}
                />
              </div>
            </div>
            {previewLoading && <p className="text-xs text-[#52525B]">Caricamento anteprima...</p>}
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setPreviewStudent(null)} className="rounded-sm">Annulla</Button>
            <Button type="button" onClick={sendFromPreview} disabled={previewSending || previewLoading || !previewRecipients.length} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="preview-send-btn">
              <PaperPlaneTilt size={16} className="mr-1" />{previewSending ? 'Invio...' : 'Invia Mail'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Tariffe per lezione Modal */}
      <Dialog open={!!tariffeStudent} onOpenChange={(o) => { if (!o) { setTariffeStudent(null); loadStudentsListinoStatus(); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Tariffe per Lezione — {tariffeStudent?.name?.toUpperCase()}{anno ? ` (A.A. ${anno + 1}/${String(anno + 2).slice(2)})` : ''}</DialogTitle>
            <DialogDescription>
              Assegna un listino dell&apos;anno successivo a ciascuna lezione. La <strong>quota associativa</strong> verrà inclusa una sola volta nell&apos;email (sulla prima lezione). Le tariffe devono essere impostate prima di poter inviare la conferma.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {tariffeLoading ? (
              <p className="text-sm text-[#52525B] text-center py-4">Caricamento...</p>
            ) : tariffeLessons.length === 0 ? (
              <p className="text-sm text-[#52525B] text-center py-4">Nessuna lezione trovata per l&apos;anno {anno}.</p>
            ) : (
              tariffeLessons.map(l => {
                const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
                return (
                  <div key={l.id} className="border border-[#E4E4E7] rounded-sm p-3 space-y-2" data-testid={`tariffa-lesson-${l.id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{DAYS[l.day]} {l.start_time}-{l.end_time}</span>
                      <span className="uppercase text-xs text-[#52525B]">{l.teacher_name}</span>
                    </div>
                    <select
                      value={l.listino_id || ''}
                      onChange={(e) => updateTariffaLesson(l.id, e.target.value)}
                      className="w-full rounded-sm border border-[#E4E4E7] px-2 py-1 text-sm"
                      data-testid={`tariffa-select-${l.id}`}
                    >
                      <option value="">— Seleziona listino —</option>
                      {tariffeAllListini.map(lst => (
                        <option key={lst.id} value={lst.id}>{lst.codice}{lst.descrizione ? ' - ' + lst.descrizione : ''}{lst.prezzo_rata ? ' (' + lst.prezzo_rata + '€)' : ''}</option>
                      ))}
                    </select>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => { setTariffeStudent(null); loadStudentsListinoStatus(); }} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm">Fatto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
